<script>
import _ from 'lodash';
import { counterMixin } from '@mixins/counter-mixin';
import { breakpointsMixin } from '@mixins/breakpointsMixin';
import {
  clientComputed,
  homeComputed,
  homeMethods,
  programComputed,
  programMethods,
  workoutComputed,
  workoutMethods,
} from '@state/helpers';

export default {
  mixins: [counterMixin, breakpointsMixin],
  props: {
    workout: {
      type: Object,
      required: false,
      default: () => {},
    },
    isSliding: {
      type: Boolean,
      required: false,
    },
  },
  computed: {
    ...clientComputed,
    ...homeComputed,
    ...programComputed,
    ...workoutComputed,
    _() {
      return _;
    },
    workoutCoverPhoto() {
      return this.homeOverview && !this.homeOverview.isProgramEmpty
        ? this.workout && this.workout.programWeekDayWorkout && this.workout.programWeekDayWorkout.hasWorkout
          ? this.workout.programWeekDayWorkout.mediaAssets &&
            this.workout.programWeekDayWorkout.mediaAssets.coverPhoto &&
            this.workout.programWeekDayWorkout.mediaAssets.coverPhoto.url
            ? this.workout.programWeekDayWorkout.mediaAssets.coverPhoto.url
            : require('@assets/images/backgrounds/ScreenLegsAndAbs.png')
          : require('@assets/images/backgrounds/RestCardBackground.jpg')
        : require('@assets/images/backgrounds/EmptyPlanCard.png');
    },
    hasWorkout() {
      return this.workout && this.workout.programWeekDayWorkout && this.workout.programWeekDayWorkout.hasWorkout;
    },
    workoutEquipments() {
      return (
        this.workout &&
        this.workout.equipmentList &&
        this.workout.equipmentList.length > 0 &&
        this.workout.equipmentList.join(', ')
      );
    },
    duration() {
      return this.workout &&
        this.workout.programWeekDayWorkout &&
        this.workout.programWeekDayWorkout.timeToComplete &&
        parseInt(this.workout.programWeekDayWorkout.timeToComplete) > 60
        ? parseInt(this.workout.programWeekDayWorkout.timeToComplete) / 60 + ' Min'
        : '0 Min';
    },
    cardTitle() {
      return this.homeOverview && this.homeOverview.isProgramEmpty
        ? 'Your Plan is Almost Done!'
        : this.workout && this.workout.programWeekDayWorkout && this.workout.programWeekDayWorkout.label
        ? this.workout.programWeekDayWorkout.label
        : 'Rest Day';
    },
    cardSubTitle() {
      return this.homeOverview && this.homeOverview.isProgramEmpty
        ? 'I am almost complete uploading your new program!'
        : this.hasWorkout
        ? `${this.duration} ${this.workoutEquipments ? '・' + this.workoutEquipments : ''}`
        : 'Take it easy today. You deserve it!';
    },
    isWorkoutCompleted() {
      return (
        this.workout &&
        this.workout.programWeekDayWorkout &&
        this.workout.programWeekDayWorkout.hasWorkout &&
        this.workout.programWeekDayWorkout.clientInput &&
        this.workout.programWeekDayWorkout.clientInput.completedAt &&
        this.workout.programWeekDayWorkout.clientInput.completedAt !== ''
      );
    },
    isWorkoutStarted() {
      return (
        this.workout &&
        this.workout.programWeekDayWorkout &&
        this.workout.programWeekDayWorkout.hasWorkout &&
        this.workout.programWeekDayWorkout.clientInput &&
        this.workout.programWeekDayWorkout.clientInput.startedAt
      );
    },
  },
  methods: {
    ...homeMethods,
    ...programMethods,
    ...workoutMethods,
    async getWorkoutDetails() {
      const payload = {
        programDay_id: this.workout.programDay_id,
        org_id: this.orgId,
        trainer_id: this.trainerId,
        user_id: this.clientId,
      };
      try {
        await this.getProgramDay(payload);
      } catch (err) {
        console.log(err);
      }
    },
    async onStartWorkout(e) {
      e.preventDefault();
      if (this.workout && this.workout.programDay_id) {
        this.setIsLoadingExercise(true);
        await this.getWorkoutDetails();
        await this.startWorkout({
          programDayId: this.workout.programDay_id,
        });
        this.$router
          .push({
            name: 'exercise',
            params: {
              id: this.selectedWorkout?.startingExercise._id,
              workout: { ...this.selectedWorkout, programDayId: this.workout.programDay_id },
              prevRoute: this.$route.name,
            },
          })
          .then(() => this.setIsLoadingExercise(false));
      }
    },
    async toWorkoutDetail(e) {
      e.preventDefault();
      if ((!this.isSliding || this.isDesktopView) && this.workout && this.workout.programDay_id) {
        this.$router.push({
          name: 'workoutDetail',
          params: { id: this.workout.programDay_id, workoutOption: this.workout },
        });
      }
    },
  },
  watch: {
    isWorkoutUpdate() {
      this.getHomeOverview();
    },
  },
};
</script>

<template>
  <div
    @click.stop="toWorkoutDetail($event)"
    :class="[
      'start-workout-card',
      {
        isRest:
          _.isEmpty(workout) ||
          (_.has(workout, 'programWeekDayWorkout') &&
            _.has(workout.programWeekDayWorkout, 'hasWorkout') &&
            !workout.programWeekDayWorkout.hasWorkout) ||
          (!_.isEmpty(workout) && workout.isRestDay),
      },
    ]"
    :style="{ 'background-image': `url(${workoutCoverPhoto})` }"
  >
    <div v-if="isWorkoutCompleted" class="col-1 check-col">
      <div class="check">
        <svg width="66" height="66" viewBox="0 0 66 66" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon-check'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M33 66c18.225 0 33-14.775 33-33S51.225 0 33 0 0 14.775 0 33s14.775 33 33 33z" fill="url(#paint0_linear)" fill-opacity=".6"/><path d="M17.368 33.55l4.466-5.321 8.645 7.255 15.748-18.116 5.243 4.56L35.803 39.95l-4.466 5.322L17.368 33.55z" fill="#fff"/><defs><linearGradient id="paint0_linear" x1="33" y1="0" x2="33" y2="66" gradientUnits="userSpaceOnUse"><stop stop-color="#55BCF5"/><stop offset="1" stop-color="#3E4FE2"/></linearGradient></defs></svg>
      </div>
    </div>
    <div :class="['title', { isEmpty: homeOverview.isProgramEmpty }, { isMobile: !isDesktopView }]">{{ cardTitle }}</div>
    <div v-if="cardSubTitle" class="description">
      <p>{{ cardSubTitle }}</p>
    </div>
    <BaseButton
      v-if="hasWorkout && !isWorkoutCompleted"
      @click.stop="onStartWorkout"
      class="button btn-primary large start-workout"
    >
      <p>
        <BaseIcon class="icon-caret-right" name="caret-right" />
        {{ isWorkoutStarted ? 'Resume' : 'Start' }} Workout
      </p>
    </BaseButton>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';
// stylelint-disable selector-pseudo-element-no-unknown

.start-workout-card {
  background-size: cover;
  border-radius: 12.4771px;
  box-shadow: 0 10px 10px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: flex-end;
  min-height: 30rem;
  position: relative;
  width: 100%;
  z-index: 4;
  &:not(.isRest) {
    cursor: pointer;
    &::after {
      background-color: rgba(0, 0, 0, 0.5);
      border-radius: 12.4771px;
      bottom: 0;
      content: '';
      height: 100%;
      min-height: 30rem;
      position: absolute;
      right: 0;
      top: 0;
      width: calc(100%);
      z-index: -1;
    }
  }
  .check-col {
    flex: 0 0 5.333333%;
    max-width: 5.333333%;
    position: absolute;
    right: 5%;
    top: 4%;
    .check {
      align-items: center;
      background: rgba(#f2f2f2, 0.2);
      border: unset;
      border-radius: 50%;
      display: flex;
      height: 20px;
      justify-content: center;
      position: relative;
      width: 20px;
      z-index: 9999;
      svg {
        background: rgba(#f2f2f2, 0.2);
        border-radius: 50%;
        height: 20px;
        width: 20px;
        path {
          &:first-child {
            fill: #f2f2f2;
          }
          &:not(:first-child) {
            fill: rgba(28, 28, 28, 0.7);
          }
        }
      }
    }
  }
  .title {
    -webkit-box-orient: vertical;
    color: #fff;
    display: block;
    display: -webkit-box;
    font-family: $base-font-family;
    font-style: normal;
    font-weight: bold;
    height: fit-content;
    line-height: 3rem;
    margin-bottom: 1.5rem;
    padding-left: 5%;
    position: relative;
    text-align: start;
    text-transform: capitalize;
    word-break: break-all;
    @include calc-font-size(3.2);
    &.isEmpty {
      &.isMobile {
        @include calc-font-size(2.3);

        height: fit-content;
        line-height: 3rem;
        margin-bottom: 1.5rem;
      }
    }
    &:not(.isEmpty) {
      line-height: 5rem;
      line-height: 4rem;
      margin-bottom: unset;
      max-width: 100%;
      text-transform: capitalize;
      z-index: 12;
      @include calc-font-size(3.2);
      &.isMobile {
        font-size: 20px;
        line-height: 22px;
        width: 100%;
      }
    }
  }

  .start-workout {
    align-items: center;
    bottom: -8%;
    display: flex;
    height: 6vw;
    justify-content: center;
    max-height: 5.5rem;
    padding: unset;
    position: absolute;
    right: 7%;
    width: 43%;
    z-index: 2;
    .icon-caret-right {
      height: 2.7rem;
      margin-right: 7px;
      width: 2.7rem;
    }
    p {
      align-items: center;
      color: #fff;
      display: flex;
      font-style: normal;
      font-weight: 500;
      justify-content: center;
      margin-bottom: unset;
      text-align: center;
      text-transform: capitalize;
      white-space: nowrap;
      @include calc-font-size(1.2);
    }
  }

  .description {
    bottom: 10%;
    color: #fff;
    display: inline-block;
    font-family: $secondary-font-family;
    font-size: 28.7156px;
    font-style: normal;
    font-weight: 300;
    line-height: 22px;
    margin-bottom: 5%;
    margin-top: 1%;
    max-width: 90%;
    padding-left: 5%;
    z-index: 2;
    p {
      @include calc-font-size(1.2);

      line-height: 1.7rem;
    }
  }
}
</style>
